import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import endpoints from '../../../../../../api/endpoints';
import { useFetch } from '../../../../../../api/useFetch';
import CommonReports from '../../../../../common/reports';
import { downloadReport } from '../../../../../common/reports/utils/downloadReport';
import { HandleDownloadReport } from '../../../../../common/reports/actions/interfaces';
import { getPermissionToGenerateReports } from '../../../../../common/reports/utils/mapReportToTableStructure';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import { FiltersFormValues, FiltersInitialData, ReportsData } from '../../../../../common/reports/interfaces';

import locales from './locales';
import FilterDetails from './details/summary';
import FiltersSummary from './details/filters';
import { mapTableDataIntoApiFormat } from './dataTransformator';
import FiltersForm from './create-report-filter-form/form';
import { validationSchema } from './create-report-filter-form/validationSchema';
import { AirtimeReportCreateForm, AirtimeReportDetailsMetadataResponse } from './interfaces';

const initFilters = {
  name: '',
  operators: [],
  skus: [],
  statuses: [],
  operationIds: [],
  from: null,
  to: null,
  acquirerMsisdns: []
};

const AirtimeReports = () => {
  const location = useLocation<{ filters: FiltersInitialData } | undefined>();
  const [initialValues, setInitialValues] = useState(location.state?.filters || initFilters);
  const { table, ...commonReports } = locales;
  const { data = {} as ReportsData, loading, error, refresh } = useFetch<ReportsData>({
    pathname: endpoints.airtimeAndDataReports
  });

  const handleDeleteReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.airtimeAndDataReports}/${id}`, {
      method: 'delete'
    });

  const handleCancelReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.airtimeAndDataReports}/${id}`, {
      method: 'delete'
    });

  const handleRetryReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.airtimeAndDataReportsRetry(id)}`, {
      method: 'POST'
    });

  const handleRegenerateReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.airtimeAndDataReportsRegenerate(id)}`, {
      method: 'POST'
    });

  const handleGenerateReportRequest = (values: AirtimeReportCreateForm) =>
    fetchWithTokenRefresh(endpoints.airtimeAndDataReports, addParams('POST', mapTableDataIntoApiFormat(values)));

  const handleDownloadReport: HandleDownloadReport = (id, name, size) => {
    downloadReport(id, `${name}.csv`, 'airtimeAndData', size);
  };

  return (
    <CommonReports
      error={error}
      filters={{
        form: (filtersFormValues: FiltersFormValues<AirtimeReportCreateForm>) => <FiltersForm {...filtersFormValues} />,
        validation: validationSchema,
        details: (metadata: AirtimeReportDetailsMetadataResponse) => <FilterDetails filters={metadata} />,
        summary: (metadata: AirtimeReportDetailsMetadataResponse) => <FiltersSummary filters={metadata} />,
        initialValues,
        clearValues: () => {
          setInitialValues(initFilters);
        },
        predefinedReportGeneration: Boolean(location.state?.filters)
      }}
      locales={commonReports}
      data={data}
      emptyMsg={table.emptyMsg}
      loading={loading}
      refresh={refresh}
      handleDeleteReportRequest={handleDeleteReportRequest}
      handleCancelReportRequest={handleCancelReportRequest}
      handleRetryReportRequest={handleRetryReportRequest}
      handleRegenerateReportRequest={handleRegenerateReportRequest}
      handleGenerateReportRequest={handleGenerateReportRequest}
      handleDownloadReport={handleDownloadReport}
      generationAllowed={getPermissionToGenerateReports(data.limits?.status)}
    />
  );
};

export default AirtimeReports;
